import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Input,
  Spinner,
  Tooltip
} from '@chakra-ui/react'
import {
  FiArrowLeft,
  FiArrowRight,
  FiMapPin,
  FiUsers,
  FiActivity,
  FiZap,
  FiRefreshCw
} from 'react-icons/fi'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { ChipInput } from '../../components/onboarding/ChipInput'
import { InfoTooltip } from '../../components/InfoTooltip'
import { createToaster, Toaster } from '@chakra-ui/react'
import { apiConfig } from '../../config/api'
import { useOnboarding } from '../../contexts/OnboardingContext'
import type { TargetingConfig, AISuggestions, Interest } from '../../types/targeting'
import { detectBrowserLocation } from '../../utils/locationDetection'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

export const TargetingPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { loadProgress, basic_info, detailed_interview, onboardingMode, isLoading: isContextLoading } = useOnboarding()

  const [targeting, setTargeting] = useState<TargetingConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestions | null>(null)
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false)

  // Track which fields user has manually edited (to preserve during regeneration)
  const [touchedFields, setTouchedFields] = useState<{
    interests?: boolean
    demographics?: boolean
    locations?: boolean
    age?: boolean
    gender?: boolean
  }>({})

  // Use ref to track if we've already generated suggestions in this session
  // This prevents regenerating every time the component re-renders or basic_info changes
  const hasGeneratedSuggestionsRef = useRef(false)
  
  // Track if we've already detected browser location
  const hasDetectedLocationRef = useRef(false)

  // Get path prefix
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding'
  }
  const pathPrefix = getPathPrefix()

  useEffect(() => {
    fetchTargeting()
  }, [])

  // Helper function to apply AI suggestions to targeting fields
  // forceUpdate: if true, updates all fields; if false, only updates untouched fields
  const applySuggestionsToTargeting = useCallback((suggestions: AISuggestions, forceUpdate: boolean = false) => {
    const suggestedInterests: Interest[] = (suggestions.interests || [])
      .slice(0, 5)
      .map((name: string, idx: number) => ({
        id: `suggested-${idx}`,
        name
      }))

    const suggestedDemographics = (suggestions.demographics || []).slice(0, 5)
    const suggestedLocations = (suggestions.locations || []).slice(0, 3)

    let suggestedGenders = [1, 2]
    if (suggestions.gender === 'women') suggestedGenders = [2]
    else if (suggestions.gender === 'men') suggestedGenders = [1]

    setTargeting(prev => {
      if (!prev) return null

      // Check if fields are "empty" (using defaults)
      const hasInterests = prev.interests.length > 0
      const hasDemographics = prev.demographics.length > 0
      const hasLocations = prev.locations.length > 0
      const hasCustomGender = !(prev.genders.length === 2 && prev.genders.includes(1) && prev.genders.includes(2))

      return {
        ...prev,
        // If forceUpdate, always apply; otherwise check if field was touched
        interests: (forceUpdate || !touchedFields.interests) && !hasInterests ? suggestedInterests : prev.interests,
        demographics: (forceUpdate || !touchedFields.demographics) && !hasDemographics ? suggestedDemographics : prev.demographics,
        locations: (forceUpdate || !touchedFields.locations) && !hasLocations ? suggestedLocations : prev.locations,
        genders: (forceUpdate || !touchedFields.gender) && !hasCustomGender ? suggestedGenders : prev.genders,
        // Always update the AI explanation
        explanation: suggestions.explanation || prev.explanation,
        // Update age if not touched
        age_min: (forceUpdate || !touchedFields.age) && (prev.age_min === 25 && prev.age_max === 55) ? (suggestions.age_min || prev.age_min) : prev.age_min,
        age_max: (forceUpdate || !touchedFields.age) && (prev.age_min === 25 && prev.age_max === 55) ? (suggestions.age_max || prev.age_max) : prev.age_max
      }
    })
  }, [touchedFields])

  // Auto-generate AI suggestions ONCE when page loads and we have basic info
  useEffect(() => {
    // Wait for context to load and targeting to be fetched
    if (isContextLoading || !targeting || hasGeneratedSuggestionsRef.current || isGeneratingSuggestions) return

    // Check if we have cached suggestions - if so, use them and skip generation
    if ((targeting as any).ai_suggestions) {
      const cachedSuggestions = (targeting as any).ai_suggestions
      setAiSuggestions(cachedSuggestions)
      applySuggestionsToTargeting(cachedSuggestions)
      hasGeneratedSuggestionsRef.current = true
      return
    }

    // Check if targeting fields are already populated - if so, don't regenerate
    // This prevents regenerating on every visit if user has already set targeting
    // Note: We don't check locations because they can be auto-detected from browser
    // Only check for interests, demographics, or explanation (which indicate user/AI input)
    const hasExistingTargeting = 
      (targeting.interests && targeting.interests.length > 0) ||
      (targeting.demographics && targeting.demographics.length > 0) ||
      (targeting.explanation && targeting.explanation.trim().length > 0)

    if (hasExistingTargeting) {
      // User has already set targeting or AI has generated suggestions, don't regenerate
      hasGeneratedSuggestionsRef.current = true
      return
    }

    // Only generate if fields are empty and we have basic info
    // Use a ref to check basic_info without adding it to dependencies
    const hasInfo = basic_info?.who_you_help || basic_info?.name || basic_info?.offer_primary_outcome

    if (hasInfo) {
      generateAISuggestions()
    }
    // Only depend on targeting being loaded - not on basic_info to avoid re-running
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targeting, isContextLoading, applySuggestionsToTargeting])

  const fetchTargeting = async () => {
    setIsLoading(true)
    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(apiConfig.endpoints.onboarding.targeting, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('Failed to load targeting')
      
      const data = await response.json()
      
      // If no locations are set, try to detect from browser
      let initialLocations = data.locations || []
      let initialLocation = data.location || ''
      
      if (initialLocations.length === 0 && !hasDetectedLocationRef.current) {
        hasDetectedLocationRef.current = true
        try {
          const detected = await detectBrowserLocation()
          if (detected.country) {
            initialLocation = detected.country
            initialLocations = [detected.country]
            // Add city if available and different from country
            if (detected.city && detected.city !== detected.country) {
              initialLocations = [detected.city, detected.country]
            }
          }
        } catch (error) {
          // Silently fail - user can still manually add locations
          console.debug('Browser location detection failed:', error)
        }
      }
      
      // Ensure arrays and required fields are initialized
      setTargeting({
        ...data,
        location: initialLocation,
        locations: initialLocations,
        age_min: data.age_min ?? 25,
        age_max: data.age_max ?? 55,
        demographics: data.demographics || [],
        interests: data.interests || [],
        custom_audiences: data.custom_audiences || [],
        genders: data.genders || [1, 2]
      })
    } catch (error) {
      console.error('Error fetching targeting:', error)
      // Initialize with defaults if fetch fails, but try to detect location
      let defaultLocation = ''
      let defaultLocations: string[] = []
      
      if (!hasDetectedLocationRef.current) {
        hasDetectedLocationRef.current = true
        try {
          const detected = await detectBrowserLocation()
          if (detected.country) {
            defaultLocation = detected.country
            defaultLocations = [detected.country]
            if (detected.city && detected.city !== detected.country) {
              defaultLocations = [detected.city, detected.country]
            }
          }
        } catch (error) {
          // Silently fail
          console.debug('Browser location detection failed:', error)
        }
      }
      
      setTargeting({
        location: defaultLocation,
        locations: defaultLocations,
        age_min: 25,
        age_max: 55,
        genders: [1, 2],
        interests: [],
        custom_audiences: [],
        demographics: []
      })
    } finally {
      setIsLoading(false)
    }
  }

  const generateAISuggestions = useCallback(async () => {
    // Mark that we've attempted generation to prevent re-running
    hasGeneratedSuggestionsRef.current = true
    setIsGeneratingSuggestions(true)
    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/targeting/suggestions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          who_you_help: basic_info?.who_you_help || '',
          offer_primary_outcome: basic_info?.offer_primary_outcome || '',
          name: basic_info?.name || '',
          mode: onboardingMode,
          detailed_interview: detailed_interview || {}
        })
      })

      if (response.ok) {
        const suggestions = await response.json()
        setAiSuggestions(suggestions)

        // Apply suggestions to targeting fields using helper
        applySuggestionsToTargeting(suggestions)
      }
    } catch (error) {
      console.error('Error generating AI suggestions:', error)
      // Silently fail - suggestions are optional
    } finally {
      setIsGeneratingSuggestions(false)
    }
  }, [basic_info, detailed_interview, onboardingMode, applySuggestionsToTargeting])

  const handleRegenerateSuggestions = useCallback(async () => {
    setIsGeneratingSuggestions(true)
    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/targeting/suggestions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          who_you_help: basic_info?.who_you_help || '',
          offer_primary_outcome: basic_info?.offer_primary_outcome || '',
          name: basic_info?.name || '',
          mode: onboardingMode,
          detailed_interview: detailed_interview || {}
        })
      })

      if (response.ok) {
        const suggestions = await response.json()
        setAiSuggestions(suggestions)

        // Apply suggestions but preserve manually edited fields
        applySuggestionsToTargeting(suggestions, false)

        // Show success feedback
        toaster.create({
          title: 'Suggestions updated',
          description: 'Generated fresh targeting suggestions based on your profile.',
          type: 'success',
        })
      } else {
        throw new Error('Failed to regenerate suggestions')
      }
    } catch (error) {
      console.error('Error regenerating AI suggestions:', error)
      toaster.create({
        title: 'Regeneration failed',
        description: 'Could not generate new suggestions. Please try again.',
        type: 'error',
      })
    } finally {
      setIsGeneratingSuggestions(false)
    }
  }, [basic_info, detailed_interview, onboardingMode, applySuggestionsToTargeting])

  const handleSave = async () => {
    if (!targeting) return
    
    setIsSaving(true)
    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(apiConfig.endpoints.onboarding.targeting, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(targeting)
      })

      if (!response.ok) throw new Error('Failed to save targeting')
      
      // Reload progress to update step status
      await loadProgress()
      
      navigate(`${pathPrefix}/package-selection`)
    } catch (error) {
      console.error('Error saving targeting:', error)
      toaster.create({
        title: 'Save failed',
        description: 'Could not save your targeting settings.',
        type: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    navigate(`${pathPrefix}/landing-page`)
  }

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      stepLabels={['Setup', 'Preview', 'Landing', 'Targeting', 'Package']}
    >
      <VStack gap={8} align="stretch" maxW="4xl" mx="auto" px={{ base: 4, sm: 6 }}>
        <VStack gap={4} textAlign="center">
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            Who should see your ad?
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            We've generated targeting suggestions based on your profile. Review and adjust to reach the right people.
          </Text>
        </VStack>

        {isLoading ? (
          <Box textAlign="center" py={20}>
            <Spinner size="xl" color="brand.500" />
            <Text mt={4} color="gray.500">Loading targeting settings...</Text>
          </Box>
        ) : targeting ? (
          <VStack gap={6} align="stretch">
            {/* AI Explanation Card */}
            {(targeting.explanation || isGeneratingSuggestions || (basic_info?.who_you_help || basic_info?.name || basic_info?.offer_primary_outcome)) && (
              <Box bg="purple.50" p={4} borderRadius="lg" borderLeft="4px solid" borderColor="purple.500">
                <HStack align="start" gap={3} justify="space-between">
                  <HStack align="start" gap={3} flex={1}>
                    <Icon as={FiZap} color="purple.500" mt={1} />
                    {isGeneratingSuggestions ? (
                      <HStack gap={2}>
                        <Spinner size="sm" color="purple.500" />
                        <Text fontSize="sm" color="purple.800">
                          Generating personalized targeting suggestions...
                        </Text>
                      </HStack>
                    ) : targeting.explanation ? (
                      <Text fontSize="sm" color="purple.800">
                        <strong>AI Insight:</strong> {targeting.explanation}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="purple.800">
                        Generate AI-powered targeting suggestions based on your profile.
                      </Text>
                    )}
                  </HStack>
                  {!isGeneratingSuggestions && (basic_info?.who_you_help || basic_info?.name || basic_info?.offer_primary_outcome) && (
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="purple"
                          onClick={handleRegenerateSuggestions}
                          disabled={isGeneratingSuggestions}
                        >
                          <Icon as={FiRefreshCw} />
                          {targeting.explanation ? 'Regenerate' : 'Generate'}
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Positioner>
                        <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm" maxW="280px">
                          <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                          {targeting.explanation 
                            ? 'Get fresh AI suggestions. Your manual edits will be preserved.'
                            : 'Generate AI-powered targeting suggestions based on your profile.'}
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Tooltip.Root>
                  )}
                </HStack>
              </Box>
            )}

            {/* Locations */}
            <Card.Root variant="outline">
              <Card.Header pb={2}>
                <HStack gap={3}>
                  <Icon as={FiMapPin} boxSize={5} color="brand.500" />
                  <Heading size="md">Locations</Heading>
                  <InfoTooltip
                    content="Add cities, states, or countries where your ideal customers are located. Leave empty to target worldwide."
                    ariaLabel="Locations help"
                  />
                </HStack>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={3}>
                  <Text fontSize="sm" color="gray.600">
                    Where are your ideal {onboardingMode === 'product' ? 'buyers' : 'clients'} located?
                  </Text>
                  <ChipInput
                    value={targeting.locations}
                    onChange={(locations) => {
                      setTargeting({ ...targeting, locations })
                      setTouchedFields(prev => ({ ...prev, locations: true }))
                    }}
                    placeholder="Type a location and press Enter (or leave empty for global)..."
                    helperText="Press Enter to add specific locations (e.g., United States, New York, Los Angeles). Leave empty to target globally."
                    colorPalette="green"
                  />
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Demographics */}
            <Card.Root variant="outline">
              <Card.Header pb={2}>
                <HStack gap={3}>
                  <Icon as={FiUsers} boxSize={5} color="brand.500" />
                  <Heading size="md">Demographics</Heading>
                </HStack>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={6}>
                  {/* Age Range */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={3}>Age Range</Text>
                    <HStack gap={4}>
                      <Input
                        type="number"
                        value={targeting.age_min}
                        onChange={(e) => {
                          setTargeting({ ...targeting, age_min: parseInt(e.target.value) || 18 })
                          setTouchedFields(prev => ({ ...prev, age: true }))
                        }}
                        w="100px"
                        min={18}
                        max={65}
                      />
                      <Text color="gray.500">to</Text>
                      <Input
                        type="number"
                        value={targeting.age_max}
                        onChange={(e) => {
                          setTargeting({ ...targeting, age_max: parseInt(e.target.value) || 65 })
                          setTouchedFields(prev => ({ ...prev, age: true }))
                        }}
                        w="100px"
                        min={18}
                        max={65}
                      />
                    </HStack>
                  </Box>

                  {/* Gender */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={3}>Gender</Text>
                    <HStack gap={3}>
                      <Button
                        size="sm"
                        variant={targeting.genders.includes(1) && targeting.genders.includes(2) ? 'solid' : 'outline'}
                        colorPalette={targeting.genders.includes(1) && targeting.genders.includes(2) ? 'brand' : 'gray'}
                        onClick={() => {
                          setTargeting({ ...targeting, genders: [1, 2] })
                          setTouchedFields(prev => ({ ...prev, gender: true }))
                        }}
                      >
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant={!targeting.genders.includes(1) && targeting.genders.includes(2) ? 'solid' : 'outline'}
                        colorPalette={!targeting.genders.includes(1) && targeting.genders.includes(2) ? 'brand' : 'gray'}
                        onClick={() => {
                          setTargeting({ ...targeting, genders: [2] })
                          setTouchedFields(prev => ({ ...prev, gender: true }))
                        }}
                      >
                        Women only
                      </Button>
                      <Button
                        size="sm"
                        variant={targeting.genders.includes(1) && !targeting.genders.includes(2) ? 'solid' : 'outline'}
                        colorPalette={targeting.genders.includes(1) && !targeting.genders.includes(2) ? 'brand' : 'gray'}
                        onClick={() => {
                          setTargeting({ ...targeting, genders: [1] })
                          setTouchedFields(prev => ({ ...prev, gender: true }))
                        }}
                      >
                        Men only
                      </Button>
                    </HStack>
                  </Box>

                  {/* Demographic Tags */}
                  <Box>
                    <HStack gap={2} mb={3}>
                      <Text fontSize="sm" fontWeight="medium">Demographic Tags</Text>
                      <InfoTooltip
                        content="Target people with specific life circumstances or characteristics."
                        ariaLabel="Demographic Tags help"
                      />
                    </HStack>
                    <ChipInput
                      value={targeting.demographics}
                      onChange={(demographics) => {
                        setTargeting({ ...targeting, demographics })
                        setTouchedFields(prev => ({ ...prev, demographics: true }))
                      }}
                      placeholder="Type a demographic and press Enter..."
                      helperText="Press Enter to add (e.g., Parents, College graduates, Small business owners)"
                      colorPalette="blue"
                      maxItems={10}
                    />
                  </Box>
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Interests */}
            <Card.Root variant="outline">
              <Card.Header pb={2}>
                <HStack gap={3}>
                  <Icon as={FiActivity} boxSize={5} color="brand.500" />
                  <Heading size="md">Interests & Behaviors</Heading>
                </HStack>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Target people interested in specific topics related to your {onboardingMode === 'product' ? 'product' : 'service'}.
                  </Text>
                  
                  <ChipInput
                    value={targeting.interests.map(i => i.name)}
                    onChange={(names) => {
                      const newInterests = names.map((name, idx) => {
                        const existing = targeting.interests.find(i => i.name === name)
                        return existing || { id: `custom-${Date.now()}-${idx}`, name }
                      })
                      setTargeting({ ...targeting, interests: newInterests })
                      setTouchedFields(prev => ({ ...prev, interests: true }))
                    }}
                    placeholder="Type an interest and press Enter..."
                    helperText="Press Enter to add (e.g., Yoga, Wellness, Meditation, Healthy eating)"
                    colorPalette="purple"
                    maxItems={15}
                  />
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Action Buttons */}
            <HStack gap={4} justify="space-between" pt={4} w="full">
              <Button
                variant="ghost"
                onClick={handleBack}
              >
                <Icon as={FiArrowLeft} />
                Back (edit landing page)
              </Button>
              <Button
                size="lg"
                colorPalette="brand"
                onClick={handleSave}
                loading={isSaving}
              >
                Continue to packages
                <Icon as={FiArrowRight} />
              </Button>
            </HStack>
          </VStack>
        ) : (
          <Box textAlign="center" color="red.500">
            Failed to load targeting configuration.
          </Box>
        )}
      </VStack>
      <Toaster toaster={toaster}>{() => null}</Toaster>
    </OnboardingLayout>
  )
}


